/**
 * Launch alert for a check-in cycle HR has opened (HR UAT list #23).
 *
 * Everyone in the cycle (counted, or Too Soon To Rate — they still have the
 * check-in) sees HR's message with the check-in and calibration end dates
 * until they dismiss it. Dismissing is remembered in this browser only, per
 * cycle; the server decides who is in the cycle.
 */
import React, { useEffect, useState } from 'react'
import { Megaphone, X } from 'lucide-react'
import { performanceService } from '@/services/api/performanceService'
import type { CycleLaunchAlert as Alert } from '@/services/api/performanceService'

const dismissedKey = (cycleId: number) => `cycle-launch-alert-dismissed:${cycleId}`

function isDismissed(cycleId: number): boolean {
  try {
    return window.localStorage.getItem(dismissedKey(cycleId)) === '1'
  } catch {
    return false
  }
}

function rememberDismissed(cycleId: number) {
  try {
    window.localStorage.setItem(dismissedKey(cycleId), '1')
  } catch {
    // Private window or blocked storage: the alert just shows again next time.
  }
}

const day = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })

export function CycleLaunchAlert() {
  const [alert, setAlert] = useState<Alert | null>(null)

  useEffect(() => {
    let live = true
    performanceService.getCycleLaunchAlert()
      .then((r) => { if (live && r.alert && !isDismissed(r.alert.cycle_id)) setAlert(r.alert) })
      .catch(() => { /* the alert helps; it never blocks the page */ })
    return () => { live = false }
  }, [])

  if (!alert) return null
  const dismiss = () => {
    rememberDismissed(alert.cycle_id)
    setAlert(null)
  }
  return (
    <div role="status" className="mb-3 flex items-start gap-3 rounded-lg border border-heritage-blue/30 bg-heritage-blue/5 px-4 py-3 text-sm text-gray-800">
      <Megaphone className="mt-0.5 h-5 w-5 flex-shrink-0 text-heritage-blue" />
      <div className="flex-1">
        <p className="font-semibold text-gray-900">{alert.name}</p>
        <p className="mt-0.5 whitespace-pre-line">{alert.message}</p>
        <p className="mt-1 text-xs text-gray-600">
          Check-ins close {day(alert.checkin_end_date)} · Calibration closes {day(alert.calibration_end_date)}
        </p>
      </div>
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="rounded p-1 text-gray-500 hover:bg-white hover:text-gray-800">
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export default CycleLaunchAlert
